/**
 * InboxListItem - Individual notification item in the inbox list
 */

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import NotificationsIcon from '@mui/icons-material/Notifications';
import type { Notification } from '../../../api/notificationApi';
import { formatInboxTimestamp } from '../utils';
import { truncateText } from './utils';

interface InboxListItemProps {
  notification: Notification;
  isSelected: boolean;
  isMultiSelected: boolean;
  onSelect: (id: string) => void;
  onMultiSelectToggle: (id: string) => void;
  showDivider: boolean;
}

function getCategoryIconComponent(topic?: string) {
  switch ((topic ?? '').toLowerCase()) {
    case 'news':
      return <NewspaperIcon sx={{ fontSize: 22 }} />;
    case 'tasks':
      return <AssignmentIcon sx={{ fontSize: 22 }} />;
    case 'incidents':
      return <AnnouncementIcon sx={{ fontSize: 22 }} />;
    case 'notifications':
      return <NotificationsIcon sx={{ fontSize: 22 }} />;
    default:
      return <NotificationsIcon sx={{ fontSize: 22 }} />;
  }
}

export function InboxListItem({
  notification,
  isSelected,
  isMultiSelected,
  onSelect,
  onMultiSelectToggle,
  showDivider,
}: InboxListItemProps) {
  const title = notification.payload.title || 'Untitled';
  const description =
    notification.payload.description ??
    notification.payload.info ??
    notification.payload.message ??
    '';
  const cleanDescription = description
    .replace(/^Sent by:\s*.+?(\n|$)/, '') // strip "Sent by: ..." line
    .replaceAll(/!\[[^\]]*\]\([^)]*\)/g, '') // strip markdown images
    .replaceAll(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links → text only
    .replaceAll(/#[\w-]+/g, '') // strip hashtags
    .replaceAll(/[*`>_~[\]]/g, '') // strip markdown syntax chars
    .replaceAll(/\s{2,}/g, ' ')
    .trim();
  const displayTime = formatInboxTimestamp(notification.created);
  const isUnread = !notification.read;

  return (
    <Box>
      <ListItemButton
        selected={isSelected}
        onClick={() => {
          onSelect(notification.id);
        }}
        sx={{
          py: 1.5,
          px: 1.5,
          borderLeft: '3px solid transparent',
          '&.Mui-selected': {
            backgroundColor: 'action.selected',
            borderLeftColor: 'primary.main',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {/* Unread dot — space always reserved */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center',
              width: 16,
              flexShrink: 0,
            }}
          >
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                color: 'primary.main',
                visibility: isUnread ? 'visible' : 'hidden',
              }}
            />
          </Box>

          {/* Checkbox (multi-select) */}
          <Checkbox
            size="small"
            checked={isMultiSelected}
            onClick={e => {
              e.stopPropagation();
              onMultiSelectToggle(notification.id);
            }}
            sx={{ p: 0.25, flexShrink: 0, alignSelf: 'center' }}
          />

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0, ml: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: isUnread ? 800 : 500,
                fontSize: '1.04rem',
                lineHeight: 1.3,
                color: isUnread ? 'primary.main' : 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {truncateText(title, 50)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.9rem',
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.secondary',
              }}
            >
              {truncateText(cleanDescription, 60)}
            </Typography>
          </Box>

          {/* Right: date + category icon */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexShrink: 0,
              gap: 0.25,
              ml: 1,
              minWidth: 40,
            }}
          >
            <Box sx={{ color: 'text.secondary', lineHeight: 1 }}>
              {getCategoryIconComponent(notification.payload.topic)}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '0.72rem',
                lineHeight: 1.1,
              }}
            >
              {displayTime}
            </Typography>
          </Box>
        </Box>
      </ListItemButton>
      {showDivider && <Divider />}
    </Box>
  );
}
